/**
 * Types for threads and comments left on creative assets — ad size variants
 * today, LCM emails and campaign artifacts as those migrate onto this system.
 * AMP board projects and tasks carry threads through the same tables.
 *
 * The status vocabularies below are the source of truth: the database columns
 * are plain `text` with no CHECK constraint, deliberately, so adding a status
 * is a code change rather than a migration. Everything that writes a status
 * must go through this module.
 *
 * Two rules about `pending` and `failed` are part of that contract and are
 * likewise unenforced by the schema: only an agent reply may hold either, and
 * only such a reply may have a null body. The database will accept a row that
 * breaks them, so the service holds both — `insertAgentComment` is the
 * only writer of either status, and the human-authored paths reject an empty
 * body rather than storing null.
 */

import { z } from "zod/v4";
import type { FileType } from "../files/types";

export const AssetCommentThreadStatuses = {
  Open: "open",
  Resolved: "resolved",
  Deleted: "deleted",
} as const;

export type AssetCommentThreadStatus =
  (typeof AssetCommentThreadStatuses)[keyof typeof AssetCommentThreadStatuses];

export const AssetCommentStatuses = {
  /**
   * A review agent woken by a comment that does not name it, still deciding
   * whether to answer. Becomes `pending` if it does and `declined` if not.
   */
  Deciding: "deciding",
  /** Agent reply whose body hasn't been filled in yet. */
  Pending: "pending",
  Created: "created",
  Updated: "updated",
  Deleted: "deleted",
  /** Agent reply that errored; body stays NULL. */
  Failed: "failed",
  /** Agent that read the thread and judged the comment was not for it. */
  Declined: "declined",
} as const;

export type AssetCommentStatus =
  (typeof AssetCommentStatuses)[keyof typeof AssetCommentStatuses];

/**
 * Statuses in which a comment is still part of the conversation. Reads filter
 * on these rather than excluding `deleted`, so a status added later is hidden
 * until it's explicitly listed here.
 */
export const VISIBLE_ASSET_COMMENT_STATUSES: readonly AssetCommentStatus[] = [
  AssetCommentStatuses.Deciding,
  AssetCommentStatuses.Pending,
  AssetCommentStatuses.Created,
  AssetCommentStatuses.Updated,
  AssetCommentStatuses.Failed,
];

/**
 * As {@link VISIBLE_ASSET_COMMENT_STATUSES}, for the notification feed, which
 * additionally drops the two bodiless working states.
 *
 * A pending comment is a reserved slot with no body — the thread view renders
 * it as "still working on a reply", which is worth seeing in the thread it
 * belongs to. In the feed it would be a notification about nothing: no text to
 * read, and it arrives before the reply it stands for, so acting on it means
 * opening a thread that has nothing new to say. The reply itself notifies when
 * it lands. `deciding` is the same, one step earlier, and may never become a
 * reply at all.
 */
export const FEED_ASSET_COMMENT_STATUSES: readonly AssetCommentStatus[] =
  VISIBLE_ASSET_COMMENT_STATUSES.filter(
    (status) =>
      status !== AssetCommentStatuses.Pending &&
      status !== AssetCommentStatuses.Deciding,
  );

/** As above, for threads. */
export const VISIBLE_ASSET_COMMENT_THREAD_STATUSES: readonly AssetCommentThreadStatus[] =
  [AssetCommentThreadStatuses.Open, AssetCommentThreadStatuses.Resolved];

const VISIBLE_COMMENT_STATUS_VALUES = new Set<string>(
  VISIBLE_ASSET_COMMENT_STATUSES,
);
const VISIBLE_THREAD_STATUS_VALUES = new Set<string>(
  VISIBLE_ASSET_COMMENT_THREAD_STATUSES,
);

/**
 * Whether a raw `current_status` is one the reader can still see.
 *
 * Takes the column's own `string` rather than a narrowed status, because that
 * is how it arrives: the columns are plain `text`, and a value written by a
 * newer deploy than the reader must fall through as not-visible rather than
 * forcing a cast at every call site.
 */
export function isVisibleAssetCommentStatus(status: string): boolean {
  return VISIBLE_COMMENT_STATUS_VALUES.has(status);
}

/** As above, for a thread. */
export function isVisibleAssetCommentThreadStatus(status: string): boolean {
  return VISIBLE_THREAD_STATUS_VALUES.has(status);
}

/**
 * Whether a thread is settled.
 *
 * Shared with the app, which asks this of a Hasura row on four surfaces — the
 * rail's filter, its cards, the canvas pins, and the activity log. They have to
 * agree, and a repeated `=== "resolved"` is how they stop agreeing when the
 * vocabulary moves.
 */
export function isResolvedAssetCommentThreadStatus(status: string): boolean {
  return status === AssetCommentThreadStatuses.Resolved;
}

const fraction = z.number().min(0).max(1);

/**
 * Where on its subject a thread is pinned, validated wherever an anchor comes
 * in, from the API or from the database.
 *
 * - `point`: a spot on a rendered asset, as fractions (0..1) of the frame so it
 *   survives a different display size. `t` is seconds into a video.
 * - `canvas`: a spot in empty canvas space, in canvas coordinates (the space
 *   node positions are in), so pan and zoom don't move it.
 * - `inline`: a passage of a doc or an element of an email, marked in the
 *   content itself (`data-ht-comment` on a span or element) so edits carry it
 *   along. A thread whose marker is gone is orphaned.
 * - `slide`: a persistent slide ID inside a presentation, independent of its position.
 */
export const commentAnchorSchema = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("point"),
    x: fraction,
    y: fraction,
    t: z.number().min(0).optional(),
  }),
  z.object({ kind: z.literal("canvas"), x: z.number(), y: z.number() }),
  z.object({ kind: z.literal("inline") }),
  z.object({ kind: z.literal("slide"), slideId: z.uuid() }),
]);

export type CommentAnchor = z.infer<typeof commentAnchorSchema>;
export type PointCommentAnchor = Extract<CommentAnchor, { kind: "point" }>;

/**
 * A thread with no anchor is a comment on the asset as a whole rather than on a
 * location within it.
 */
export type MaybeCommentAnchor = CommentAnchor | null;

/**
 * Narrows a raw `anchor` jsonb value read from the database. Returns null for
 * anything unrecognized, which reads as an unpinned thread — a thread whose
 * anchor can't be parsed is still worth showing.
 */
export function parseCommentAnchor(value: unknown): MaybeCommentAnchor {
  const parsed = commentAnchorSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

/** As {@link parseCommentAnchor}, for surfaces that only place point pins. */
export function parsePointCommentAnchor(
  value: unknown,
): PointCommentAnchor | null {
  const anchor = parseCommentAnchor(value);

  return anchor?.kind === "point" ? anchor : null;
}

/**
 * The asset a thread hangs off. Exactly one of these is set on a row, enforced
 * by the `asset_comment_threads_one_subject` CHECK; this union is the typed
 * mirror of that constraint so callers can't construct an invalid subject.
 *
 * `channelSizeId` scopes an ad thread to one rendered size, since a comment is
 * left on a particular render rather than on the asset across all of them. It
 * points at the channel size rather than at a size-variant row because a
 * variant row is created per version (and per in-place edit), so a
 * variant-anchored thread would detach the first time the ad was regenerated.
 *
 * It is null for the primary size, which has no channel of its own — that ad is
 * still commentable, so the column is nullable and reads match on `IS NULL`.
 */
export type CommentThreadSubject =
  | {
      kind: "adBriefAsset";
      adBriefAssetId: string;
      channelSizeId: string | null;
    }
  | { kind: "contentEmail"; contentEmailId: string }
  | { kind: "campaignArtifact"; campaignArtifactId: string }
  | { kind: "ampProject"; ampProjectId: string }
  | { kind: "ampTask"; ampTaskId: string }
  | { kind: "settingsEntity"; settingsEntityId: string }
  | { kind: "ampCanvas"; ampCanvasId: string };

/**
 * A thread's subject columns, as its database row and the app's Hasura query both carry them.
 * Exactly one id is set; `channel_size_id` goes with an ad.
 */
export type CommentThreadSubjectColumns = {
  ad_brief_asset_id: string | null;
  channel_size_id: string | null;
  content_email_id: string | null;
  campaign_artifact_id: string | null;
  amp_project_id: string | null;
  amp_task_id: string | null;
  settings_entity_id: string | null;
  amp_canvas_id: string | null;
};

/** Maps the subject union onto the mutually-exclusive subject columns. */
export function commentThreadSubjectColumns(
  subject: CommentThreadSubject,
): CommentThreadSubjectColumns {
  const none: CommentThreadSubjectColumns = {
    ad_brief_asset_id: null,
    channel_size_id: null,
    content_email_id: null,
    campaign_artifact_id: null,
    amp_project_id: null,
    amp_task_id: null,
    settings_entity_id: null,
    amp_canvas_id: null,
  };

  switch (subject.kind) {
    case "adBriefAsset":
      return {
        ...none,
        ad_brief_asset_id: subject.adBriefAssetId,
        channel_size_id: subject.channelSizeId,
      };
    case "contentEmail":
      return { ...none, content_email_id: subject.contentEmailId };
    case "campaignArtifact":
      return { ...none, campaign_artifact_id: subject.campaignArtifactId };
    case "ampProject":
      return { ...none, amp_project_id: subject.ampProjectId };
    case "ampTask":
      return { ...none, amp_task_id: subject.ampTaskId };
    case "settingsEntity":
      return { ...none, settings_entity_id: subject.settingsEntityId };
    case "ampCanvas":
      return { ...none, amp_canvas_id: subject.ampCanvasId };
  }
}

/** The subject a thread is on, read from its columns. Null when none is set. */
export function commentThreadSubjectFromColumns(
  row: CommentThreadSubjectColumns,
): CommentThreadSubject | null {
  if (row.ad_brief_asset_id !== null) {
    return {
      kind: "adBriefAsset",
      adBriefAssetId: row.ad_brief_asset_id,
      // Null for the primary size, which has no channel of its own.
      channelSizeId: row.channel_size_id,
    };
  }
  if (row.content_email_id !== null) {
    return { kind: "contentEmail", contentEmailId: row.content_email_id };
  }
  if (row.campaign_artifact_id !== null) {
    return {
      kind: "campaignArtifact",
      campaignArtifactId: row.campaign_artifact_id,
    };
  }
  if (row.amp_project_id !== null) {
    return { kind: "ampProject", ampProjectId: row.amp_project_id };
  }
  if (row.amp_task_id !== null) {
    return { kind: "ampTask", ampTaskId: row.amp_task_id };
  }
  if (row.settings_entity_id !== null) {
    return { kind: "settingsEntity", settingsEntityId: row.settings_entity_id };
  }
  if (row.amp_canvas_id !== null) {
    return { kind: "ampCanvas", ampCanvasId: row.amp_canvas_id };
  }
  return null;
}

/** A string naming a subject: two subjects have the same key exactly when they are the same subject. */
export function commentSubjectKey(subject: CommentThreadSubject): string {
  switch (subject.kind) {
    case "adBriefAsset":
      return `adBriefAsset:${subject.adBriefAssetId}:${subject.channelSizeId ?? ""}`;
    case "contentEmail":
      return `contentEmail:${subject.contentEmailId}`;
    case "campaignArtifact":
      return `campaignArtifact:${subject.campaignArtifactId}`;
    case "ampProject":
      return `ampProject:${subject.ampProjectId}`;
    case "ampTask":
      return `ampTask:${subject.ampTaskId}`;
    case "settingsEntity":
      return `settingsEntity:${subject.settingsEntityId}`;
    case "ampCanvas":
      return `ampCanvas:${subject.ampCanvasId}`;
  }
}

/** A thread on the AMP board rather than on a creative asset. */
export type AmpCommentThreadSubject = Extract<
  CommentThreadSubject,
  { kind: "ampProject" | "ampTask" }
>;

/**
 * Whether a subject is on the board rather than a creative asset. A board
 * project or task has no render and no versions, so review agents and version
 * tracking do not apply to it.
 */
export function isAmpCommentThreadSubject(
  subject: CommentThreadSubject,
): subject is AmpCommentThreadSubject {
  switch (subject.kind) {
    case "adBriefAsset":
    case "contentEmail":
    case "campaignArtifact":
    case "settingsEntity":
    case "ampCanvas":
      return false;
    case "ampProject":
    case "ampTask":
      return true;
  }
}

/** The file reference columns a subject can be matched against. */
type FileSubjectColumns = {
  ad_brief_asset_id: string | null;
  content_email_id: string | null;
  campaign_artifact_id: string | null;
  settings_entity_id: string | null;
  canvas_id: string | null;
};

/**
 * Whether a subject is on this file, so the file's agent may answer its
 * thread. A size of an ad is on the ad's file.
 */
export function isSubjectOnFile(
  subject: CommentThreadSubject,
  file: FileSubjectColumns,
): boolean {
  switch (subject.kind) {
    case "adBriefAsset":
      return file.ad_brief_asset_id === subject.adBriefAssetId;
    case "contentEmail":
      return file.content_email_id === subject.contentEmailId;
    case "campaignArtifact":
      return file.campaign_artifact_id === subject.campaignArtifactId;
    case "settingsEntity":
      return file.settings_entity_id === subject.settingsEntityId;
    case "ampCanvas":
      return file.canvas_id === subject.ampCanvasId;
    case "ampProject":
    case "ampTask":
      return false;
  }
}

/**
 * Whether review agents and version tracking apply to a subject. They need a
 * creative render with versions; a board project or task has neither, and a
 * canvas doc's versions are not tracked on its comments.
 */
export function isReviewableCommentThreadSubject(
  subject: CommentThreadSubject,
): boolean {
  switch (subject.kind) {
    case "adBriefAsset":
    case "contentEmail":
    case "campaignArtifact":
      return true;
    case "ampProject":
    case "ampTask":
    case "settingsEntity":
    case "ampCanvas":
      return false;
  }
}

/**
 * The anchor kinds a subject's threads can be pinned with. Any subject also
 * takes a thread on it as a whole, with no anchor.
 * `fileType` is the type of the file the thread is on, null when it is on none.
 */
export function anchorKindsForSubject(
  subject: CommentThreadSubject,
  fileType: FileType | null,
): readonly CommentAnchor["kind"][] {
  if (fileType === "slides") return ["slide"];
  // Uploads take comments on the file as a whole.
  if (fileType === "upload") {
    return [];
  }

  switch (subject.kind) {
    case "adBriefAsset":
      return ["point"];
    case "contentEmail":
    case "settingsEntity":
      return ["inline"];
    case "ampCanvas":
      return ["canvas"];
    case "campaignArtifact":
    case "ampProject":
    case "ampTask":
      return [];
  }
}

/**
 * The asset version a comment was written against. Tracking only — comments
 * carry across versions until their thread is resolved, so nothing filters on
 * this. A resize is its own asset, so a comment on one records that asset's
 * version.
 */
export type CommentVersionRef =
  | { kind: "adBriefAsset"; versionId: string }
  | { kind: "contentEmail"; versionId: string }
  | { kind: "campaignArtifact"; versionId: string };

/**
 * Who a comment mentions. `agent` is Hightouch's own agent, which needs no id:
 * there is one, and it answers on projects and tasks.
 */
export type CommentMention =
  | { kind: "user"; userId: number }
  | { kind: "reviewAgent"; reviewAgentId: string }
  | { kind: "agent" };

/** The name Hightouch's agent is mentioned and shown by. */
export const AGENT_NAME = "Agent";

export interface AssetCommentMention {
  id: string;
  mention: CommentMention;
  createdAt: Date;
}

export interface AssetComment {
  id: string;
  threadId: string;
  /** NULL while an agent reply is pending or after it has failed. */
  body: string | null;
  authorUserId: number | null;
  authorReviewAgentId: string | null;
  /** The session of Hightouch's agent, on a comment it wrote. */
  authorLlmSessionId: string | null;
  status: AssetCommentStatus;
  mentions: AssetCommentMention[];
  createdAt: Date;
  updatedAt: Date;
}

/**
 * An edited comment, alongside the mention targets the edit introduced.
 *
 * An edit rewrites the whole mention set from the new body, so "who did this
 * edit newly tag" is only knowable at the moment of the write — the rows
 * afterwards look the same whether a target was just added or has been there
 * since the comment was written. Reported here because it is what notifications
 * key off: re-notifying everyone the body still names would mean a fresh email
 * for every typo fix.
 */
export interface UpdatedAssetComment extends AssetComment {
  newlyMentioned: CommentMention[];
  pendingAgentReplies: PendingAgentReply[];
}

/**
 * A pending reply reserved for a mentioned review agent, reported by the write
 * that opened it so the caller can start the agent run once the transaction
 * commits.
 */
export interface PendingAgentReply {
  commentId: string;
  reviewAgentId: string;
  threadId: string;
}

/**
 * A review agent that already spoke in a thread, woken by a later comment that
 * does not name it.
 *
 * Deliberately not a {@link PendingAgentReply}: nobody asked this agent for
 * anything, so the run may end without posting, and a caller must not treat a
 * declined agent as one whose reply went missing.
 */
export interface FollowUpAgentCandidate {
  reviewAgentId: string;
  threadId: string;
  /** The comment that woke the agent, which its run is attributed to. */
  triggeringCommentId: string;
}

/** A written comment or thread, plus the agent replies its mentions reserved. */
export interface WithPendingAgentReplies {
  pendingAgentReplies: PendingAgentReply[];
}

export interface AssetCommentThread {
  id: string;
  subject: CommentThreadSubject;
  anchor: MaybeCommentAnchor;
  status: AssetCommentThreadStatus;
  createdByUserId: number | null;
  createdByReviewAgentId: string | null;
  /** The file whose agent answers the thread. */
  ampFileId: string | null;
  comments: AssetComment[];
  createdAt: Date;
  updatedAt: Date;
}
